import axios from 'axios';
import { AppConfig } from 'AppConfig';
import * as Types from './userTypes';
import * as AppActionTypes from 'store/actions/appstate';
import { setAxiosHeaderToken } from 'networking/NetworkManger';
import LocalStore from 'utils/LocalStore';
import { CLEAN_SESSION } from 'features/masterData/util/CommonUtil';
import { seedDefaultFilters } from 'features/globalFilters/globalFiltersActions';
import { beginSwitch, clearIntent, isLatestSwitch, resolveSwitchTarget } from './tenantSwitchSequence';

// The switch currently in flight, if any. Route initialisers (master.js, lookupTableNav.js)
// await it and re-read the store before deciding whether they still need to switch, so they
// never dispatch a competing switch built on a tenant that is about to change
// (OCI-BNIST-17521 / OCI-BNIST-17503). Complements the supersession + coalescing below.
let activeTenantSwitchPromise = null;

/** Resolves once no tenant/app switch is in flight. */
export const waitForTenantSwitch = async () => {
  let switchPromise = activeTenantSwitchPromise;
  while (switchPromise) {
    // The chain below never rejects (its catch returns null), so awaiting it is safe.
    await switchPromise;
    // The awaited object is the chain's .finally() promise, so by now that handler has
    // cleared activeTenantSwitchPromise -- unless a newer switch replaced it while we were
    // suspended, in which case wait for that one too. Re-read rather than re-test the
    // module variable so each iteration either exits or awaits a strictly newer promise.
    const next = activeTenantSwitchPromise;
    if (next === switchPromise) break; // defensive: never re-await a promise already settled
    switchPromise = next;
  }
  return null;
};

export const fetchData = (companyCode, appCode, { explicitTenant = true, explicitApp = true } = {}) => {
  return (dispatch, getState) => {
    // Mark the switch as in flight BEFORE the request. currentTenantCd still holds the
    // outgoing tenant for the whole round trip, so without this any screen showing
    // tenant-scoped data -- Enigma's member PHI in particular -- keeps rendering and
    // keeps issuing searches against the tenant the user just navigated away from.
    // Claim this switch. A slower earlier switch must not commit its tenant, its JWT,
    // or lower the guard once a later one has started.
    const switchRequestId = beginSwitch();
    dispatch({ type: Types.TENANT_SWITCH_START });
    const isSuperseded = () => !isLatestSwitch(switchRequestId);

    // Callers fill in the dimension they are not changing from committed state, which is
    // stale mid-switch. Ask for the coalesced (tenant, app) the user actually wants.
    const { currentTenantCd: committedTenant, currentApp: committedApp } = getState().user;
    const target = resolveSwitchTarget(
      { tenantCd: companyCode, appCode },
      { tenantCd: committedTenant, appCode: committedApp },
      { tenant: explicitTenant, app: explicitApp }
    );

    const switchPromise = axios
      .post(AppConfig.IAM_BACKEND_URL + AppConfig.APP_TENANT_SWITCHING_URL, {
        companyCode: target.tenantCd,
        appCode: target.appCode
      }, { hideSpinner: true })
      .then((res) => {
        if (isSuperseded()) return null;
        clearIntent(); // this is the latest switch and it is settling
        if (res) {
          const responseData = res.data;
          const { currentTenantCd, currentApp: responseApp } = responseData;

          if (window.clarity) {
            // Check for APP_SWITCH event

            window.clarity('event', 'APP_SWITCH');

            // Check for TENANT_SWITCH event
            window.clarity('event', 'TENANT_SWITCH');

            // Set Clarity custom tags
            window.clarity('set', 'currentTenantCd', currentTenantCd);
            window.clarity('set', 'currentApp', responseApp);
          }

          // Update session and dispatch success action
          setAxiosHeaderToken(res.data.jwt);
          LocalStore.saveAccessToken(res.data.jwt);

          dispatch({
            type: Types.FETCH_DATA_SUCCESS,
            payload: res.data
          });

          /*
           * Seed default current/prior period date filters and division/plan
           * options from the tenant's available data. No-op when the user has
           * already applied custom filters. Only meaningful for the front
           * office; other apps' tokens cannot read the filter endpoints and
           * would seed empty option lists.
           */
          if (target.appCode === AppConfig.APP_CODE_FO) {
            dispatch(seedDefaultFilters());
          }
          return responseData;
        }
        CLEAN_SESSION();
        // CLEAN_SESSION only clears storage. Without this the TENANT_SWITCH_START
        // flag above would stay raised and permanently block the hydration guard.
        dispatch({ type: Types.FETCH_DATA_FAILURE });
        return null;
      })
      .catch((err) => {
        console.log(JSON.stringify(err));
        if (isSuperseded()) return null;
        clearIntent();
        dispatch({
          type: Types.FETCH_DATA_FAILURE
        });
        dispatch({
          type: AppActionTypes.APPSTATE_SET_APP_ERROR,
          payload: err ? err.message : err
        });
        return null;
      })
      .finally(() => {
        if (activeTenantSwitchPromise === switchPromise) {
          activeTenantSwitchPromise = null;
        }
      });
    activeTenantSwitchPromise = switchPromise;
    return switchPromise;
  };
};

export const getAllBrokers = () => {
  return (dispatch) => {
    axios.get(AppConfig.FO_INITIAL_URL + `/broker/all`).then((m) => {
      if (m != null && m.data) {
        dispatch({
          type: Types.FETCH_ALL_BROKERS,
          payload: m.data
        });
      }
    });
  };
};
